/* eslint-disable no-console */
import Swal from "sweetalert2";
import axios from "../../services/api";

const MESSAGES = {
  VALIDATION: {
    REQUIRED_NAME: "من فضلك ادخل اسم المجموعة",
  },
  ERROR: {
    FETCH: "حدث خطأ أثناء تحميل البيانات",
    SAVE: "حدث خطأ أثناء حفظ البيانات",
    UPDATE: "حدث خطأ أثناء تعديل البيانات",
    DELETE: "حدث خطأ أثناء حذف البيانات",
    FETCH_PERMISSIONS: "حدث خطأ أثناء تحميل الصلاحيات",
  },
  SUCCESS: {
    SAVE: "تم حفظ المجموعة بنجاح",
    UPDATE: "تم تحديث المجموعة بنجاح",
    DELETE: "تم حذف المجموعة بنجاح",
  },
  CONFIRM: {
    DELETE_TITLE: "هل انت متأكد؟",
    DELETE_TEXT: "سيتم حذف هذه المجموعة من النظام نهائياً",
    YES: "نعم",
    NO: "لا",
  },
};

const state = {
  // User Groups State
  userGroups: [],
  isLoading: false,
  editingGroup: null,

  // Permissions State
  selectedPermissions: [],
  permissionsMap: {},
  permissionsLoading: false,

  // Form State
  form: {
    name: "",
  },
};

const getters = {
  // User Groups Getters
  getUserGroups: (state) => state.userGroups,
  getIsLoading: (state) => state.isLoading,
  getEditingGroup: (state) => state.editingGroup,
  getForm: (state) => state.form,
  isEditing: (state) => state.editingGroup !== null,
  isFormValid: (state) => state.form.name.trim().length > 0,

  // Permissions Getters
  getSelectedPermissions: (state) => state.selectedPermissions,
  getPermissionsMap: (state) => state.permissionsMap,
  getPermissionsLoading: (state) => state.permissionsLoading,

  // Computed Permissions Sections
  getPermissionsSections: (state) => {
    return Object.entries(state.permissionsMap)
      .map(([key, value]) => {
        if (!value.permissions || Object.keys(value.permissions).length === 0) {
          return null;
        }
        return {
          type: key,
          name: value.label,
          items: Object.entries(value.permissions).map(
            ([permKey, permLabel]) => ({
              label: permLabel,
              value: `${key}-${permKey}`,
            })
          ),
        };
      })
      .filter(Boolean);
  },

  getAllPermissionValues: (state, getters) => {
    return getters.getPermissionsSections.flatMap((section) =>
      section.items.map((i) => i.value)
    );
  },

  isAllPermissionsSelected: (state, getters) => {
    const allValues = getters.getAllPermissionValues;
    return (
      allValues.length > 0 &&
      allValues.every((val) => state.selectedPermissions.includes(val))
    );
  },

  getSectionSelectionState: (state, getters) => {
    const state_obj = {};
    getters.getPermissionsSections.forEach((section) => {
      const allSelected = section.items.every((item) =>
        state.selectedPermissions.includes(item.value)
      );
      state_obj[section.type] = allSelected;
    });
    return state_obj;
  },
};

const mutations = {
  // User Groups Mutations
  SET_USER_GROUPS(state, userGroups) {
    state.userGroups = userGroups;
  },

  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },

  SET_EDITING_GROUP(state, group) {
    state.editingGroup = group;
  },

  SET_FORM_NAME(state, name) {
    state.form.name = name;
  },

  RESET_FORM(state) {
    state.form.name = "";
    state.editingGroup = null;
  },

  // Permissions Mutations
  SET_SELECTED_PERMISSIONS(state, permissions) {
    state.selectedPermissions = [...permissions];
  },

  SET_PERMISSIONS_MAP(state, permissionsMap) {
    state.permissionsMap = permissionsMap;
  },

  SET_PERMISSIONS_LOADING(state, isLoading) {
    state.permissionsLoading = isLoading;
  },

  ADD_PERMISSION(state, permission) {
    if (!state.selectedPermissions.includes(permission)) {
      state.selectedPermissions.push(permission);
    }
  },

  REMOVE_PERMISSION(state, permission) {
    state.selectedPermissions = state.selectedPermissions.filter(
      (val) => val !== permission
    );
  },

  CLEAR_ALL_PERMISSIONS(state) {
    state.selectedPermissions = [];
  },

  SELECT_ALL_PERMISSIONS(state, allValues) {
    state.selectedPermissions = [...allValues];
  },
};

const actions = {
  // User Groups Actions
  async fetchUserGroups({ commit }) {
    try {
      commit("SET_LOADING", true);
      const { data } = await axios.get("/api/userGroups");
      commit("SET_USER_GROUPS", data.data || []);
    } catch (error) {
      throw new Error(MESSAGES.ERROR.FETCH);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async createUserGroup({ commit, dispatch, state }) {
    try {
      commit("SET_LOADING", true);
      const submitData = {
        name: state.form.name,
      };

      await axios.post("/api/userGroups", submitData);

      dispatch("resetForm");
      await dispatch("fetchUserGroups");

      return { success: true, message: MESSAGES.SUCCESS.SAVE };
    } catch (error) {
      throw new Error(MESSAGES.ERROR.SAVE);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async updateUserGroup({ commit, dispatch, state }) {
    if (!state.editingGroup) return;

    try {
      commit("SET_LOADING", true);
      const submitData = await dispatch("prepareSubmitData", {
        name: state.form.name,
        permissions: state.selectedPermissions,
      });

      await axios.put(`/api/userGroups/${state.editingGroup.id}`, submitData);

      dispatch("resetForm");
      await dispatch("fetchUserGroups");

      return { success: true, message: MESSAGES.SUCCESS.UPDATE };
    } catch (error) {
      throw new Error(MESSAGES.ERROR.UPDATE);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async deleteUserGroup({ commit, dispatch }, group) {
    const confirmed = await dispatch("confirmDelete");
    if (!confirmed) return { success: false };

    try {
      commit("SET_LOADING", true);
      await axios.delete(`/api/userGroups/${group.id}`);

      dispatch("resetForm");
      await dispatch("fetchUserGroups");

      return { success: true, message: MESSAGES.SUCCESS.DELETE };
    } catch (error) {
      throw new Error(MESSAGES.ERROR.DELETE);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async editUserGroup({ commit, dispatch }, group) {
    try {
      const permissionsResult = await dispatch("getGroupPermissions", group.id);

      if (permissionsResult.success) {
        commit("SET_EDITING_GROUP", group);
        commit("SET_FORM_NAME", group.name);
        dispatch("setSelectedPermissions", permissionsResult.data);
        return { success: true };
      }
    } catch (error) {
      throw new Error(MESSAGES.ERROR.UPDATE);
    }
  },

  async getGroupPermissions({ commit }, groupId) {
    try {
      commit("SET_LOADING", true);
      const { data: groupPermissions } = await axios.get(
        `/api/userGroups/${groupId}`
      );
      return {
        success: true,
        data: groupPermissions.data?.abilities || [],
      };
    } catch (error) {
      return { success: false, error };
    } finally {
      commit("SET_LOADING", false);
    }
  },

  resetForm({ commit }) {
    commit("RESET_FORM");
    commit("SET_SELECTED_PERMISSIONS", []);
  },

  setFormName({ commit }, name) {
    commit("SET_FORM_NAME", name);
  },

  // Permissions Actions
  async fetchPermissionsSections({ commit, state, dispatch }) {
    try {
      console.warn("true true");
      commit("SET_PERMISSIONS_LOADING", true);

      // Fetch permissions
      const { data } = await axios.get("/api/permissions");
      const permissions = data;

      const permissionsMap = { ...permissions };
      commit("SET_PERMISSIONS_MAP", permissionsMap);
    } catch (error) {
      throw new Error(MESSAGES.ERROR.FETCH_PERMISSIONS);
    } finally {
      commit("SET_PERMISSIONS_LOADING", false);
    }
  },

  setSelectedPermissions({ commit }, permissions) {
    if (permissions && permissions.length > 0) {
      const formattedPermissions = permissions.map(
        (item) => `${item.type}-${item.permission}`
      );
      commit("SET_SELECTED_PERMISSIONS", formattedPermissions);
    } else {
      commit("SET_SELECTED_PERMISSIONS", []);
    }
  },

  updateSelectedPermissions({ commit }, permissions) {
    commit("SET_SELECTED_PERMISSIONS", permissions);
  },

  togglePermission({ commit, state }, permission) {
    if (state.selectedPermissions.includes(permission)) {
      commit("REMOVE_PERMISSION", permission);
    } else {
      commit("ADD_PERMISSION", permission);
    }
  },

  toggleAllPermissions({ commit, state, getters }) {
    const allValues = getters.getAllPermissionValues;
    if (getters.isAllPermissionsSelected) {
      commit("CLEAR_ALL_PERMISSIONS");
    } else {
      commit("SELECT_ALL_PERMISSIONS", allValues);
    }
  },

  toggleAllInSection({ commit, state, getters }, sectionType) {
    const section = getters.getPermissionsSections.find(
      (s) => s.type === sectionType
    );
    if (!section) return;

    const values = section.items.map((i) => i.value);
    const allSelected = section.items.every((item) =>
      state.selectedPermissions.includes(item.value)
    );

    if (allSelected) {
      // Remove all items from this section
      values.forEach((value) => {
        commit("REMOVE_PERMISSION", value);
      });
    } else {
      // Add all items from this section
      values.forEach((value) => {
        commit("ADD_PERMISSION", value);
      });
    }
  },

  // Helper Actions
  async prepareSubmitData({ dispatch }, payload) {
    const abilities = await dispatch(
      "convertToOriginalFormat",
      payload.permissions
    );

    return {
      name: payload.name,
      abilities: abilities,
    };
  },

  async convertToOriginalFormat({ state }, payload) {
    return payload.map((perm) => {
      // if (perm === "DeparmentClassificationGroups") {
      //   return { type: perm, permission: perm };
      // }
      const [type, ...permissionParts] = perm.split("-");
      const permission = permissionParts.join("-");
      return { type, permission };
    });
  },

  async confirmDelete() {
    const result = await Swal.fire({
      title: MESSAGES.CONFIRM.DELETE_TITLE,
      text: MESSAGES.CONFIRM.DELETE_TEXT,
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "md-button md-success btn-fill",
      cancelButtonClass: "md-button md-danger btn-fill",
      confirmButtonText: MESSAGES.CONFIRM.YES,
      cancelButtonText: MESSAGES.CONFIRM.NO,
      buttonsStyling: false,
    });

    return result.value;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
